/**
 * studentSamViewModel — Sam sur une activité : sa conversation (`engine/activity/sam`) traduite en
 * props de `<SamConversation>` et de `<SamFloatingTile>`. Pur.
 *
 * Portage de `SamConversationSheet` (legacy) et de `SamSessionScreen` :
 *  - le fil : l'élève à droite, Sam à gauche (son texte en markdown et formules), le cliché d'un
 *    « Où j'en suis » en image ; le message que Sam n'a pas reçu (panne) est marqué « Non envoyé » et
 *    se renvoie ;
 *  - l'accueil tant que l'élève n'a pas parlé et que Sam n'a dit qu'un bonjour, avec les cartes de
 *    démarrage : « Regarde où j'en suis en photo », « Je ne comprends pas l'énoncé », « Donne-moi un
 *    indice pour démarrer », « Rappelle-moi le cours » ;
 *  - puis les pastilles : « Où j'en suis », « Indice », « Rappel de cours » (« Explique l'énoncé » ne
 *    sert qu'à démarrer) ;
 *  - Sam réfléchit (aussi pendant la lecture d'une photo, que la carte d'attente raconte) ; le
 *    plafond ; « Sam met du temps à répondre » quand le serveur l'a dit (dégradé) ;
 *  - l'aperçu de la tuile : le dernier mot de Sam, feuille fermée.
 */
import { groupConversation } from "../../../ui/formatters/conversationFormatter"
import { type UIConversationDay, type UIConversationMessage } from "../../../ui/types/studentConversation"

/** Un message du fil de Sam, tel que le réducteur `sam` le garde. */
export type SamThreadMessage = {
    id: string
    role: "student" | "sam"
    content?: string
    origin?: string | null
    kind?: string
    imageUri?: string
}

export type SamAction = "work_check" | "explain" | "hint" | "course_reminder"

export const SAM_TEXTS = {
    explainMessage: "Je ne comprends pas l'énoncé, tu peux me l'expliquer ?",
    starters: {
        work_check: "Regarde où j'en suis en photo",
        explain: "Je ne comprends pas l'énoncé",
        hint: "Donne-moi un indice pour démarrer",
        course_reminder: "Rappelle-moi le cours",
    } satisfies Record<SamAction, string>,
    chips: {
        work_check: "Où j'en suis",
        workCheckSending: "Envoi…",
        hint: "Indice",
        course_reminder: "Rappel de cours",
    },
}

const ICONS: Record<SamAction, "camera" | "file-lines" | "lightbulb" | "book-open"> = {
    work_check: "camera",
    explain: "file-lines",
    hint: "lightbulb",
    course_reminder: "book-open",
}

/** Le fil, comme `<SamConversation>` le lit (sans jour ni heure). */
export function samThreadDays(messages: SamThreadMessage[], failedMessageId: string | null): UIConversationDay[] {
    const rows: UIConversationMessage[] = messages
        .map((message): UIConversationMessage | null => {
            if (message.kind === "scan") {
                if (!message.imageUri) return null
                return { id: message.id, author: "self", attachment: { kind: "image", uri: message.imageUri } }
            }
            if (!message.content) return null
            return {
                id: message.id,
                author: message.role === "student" ? "self" : "other",
                text: message.content,
                format: message.role === "sam" ? "rich" : "plain",
                status: message.id === failedMessageId ? "failed" : undefined,
            }
        })
        .filter((row): row is UIConversationMessage => row != null)
    return groupConversation(rows, { dated: false })
}

/** L'accueil : l'élève n'a pas parlé, et Sam n'a dit au plus qu'un bonjour. */
export function samShowsWelcome(messages: SamThreadMessage[], thinking: boolean): boolean {
    const samMessages = messages.filter((message) => message.role === "sam")
    return (
        !messages.some((message) => message.role === "student") &&
        samMessages.every((message) => message.origin === "proactive") &&
        samMessages.length <= 1 &&
        !thinking
    )
}

export type SamModelInput = {
    messages: SamThreadMessage[]
    isThinking: boolean
    degraded: boolean
    capScope: "activity" | "daily" | null
    failedMessageId: string | null
    workCheckUploading: boolean
    /** La lecture de la photo dure (plus de 9 s) : Sam le dit. */
    workCheckLate: boolean
}

export type SamConversationModel = {
    days: UIConversationDay[]
    welcome: boolean
    starters: { key: SamAction; label: string; iconName: (typeof ICONS)[SamAction] }[]
    quickReplies: {
        key: SamAction
        label: string
        iconName: (typeof ICONS)[SamAction]
        emphasis?: boolean
        loading?: boolean
        disabled?: boolean
    }[]
    thinking: boolean
    workCheck: "reading" | "late" | null
    cap: "activity" | "daily" | null
    slow: boolean
}

export function samConversationModel(input: SamModelInput): SamConversationModel {
    const thinking = input.isThinking || input.workCheckUploading
    const capped = input.capScope != null
    const disabled = capped || thinking
    return {
        days: samThreadDays(input.messages, input.failedMessageId),
        welcome: samShowsWelcome(input.messages, thinking),
        starters: (["work_check", "explain", "hint", "course_reminder"] as const).map((key) => ({
            key,
            label: SAM_TEXTS.starters[key],
            iconName: ICONS[key],
        })),
        quickReplies: [
            {
                key: "work_check",
                label: input.workCheckUploading ? SAM_TEXTS.chips.workCheckSending : SAM_TEXTS.chips.work_check,
                iconName: ICONS.work_check,
                emphasis: true,
                loading: input.workCheckUploading,
                disabled: disabled || input.workCheckUploading,
            },
            { key: "hint", label: SAM_TEXTS.chips.hint, iconName: ICONS.hint, disabled },
            {
                key: "course_reminder",
                label: SAM_TEXTS.chips.course_reminder,
                iconName: ICONS.course_reminder,
                disabled,
            },
        ],
        thinking: input.isThinking,
        workCheck: input.workCheckUploading ? (input.workCheckLate ? "late" : "reading") : null,
        cap: input.capScope,
        slow: input.degraded && !capped && input.failedMessageId == null,
    }
}

/** L'aperçu de la tuile : le dernier mot de Sam, quand c'est lui qui a parlé en dernier. */
export function samPeek(messages: SamThreadMessage[]): string | null {
    const last = messages[messages.length - 1]
    return last?.role === "sam" && last.content ? last.content : null
}
